import { For, Show } from "solid-js"
import { Button } from "@openanalyst-ai/ui/button"

type ActivityItem = {
  id?: string
  title: string
  description?: string
  status: "success" | "info" | "warning" | "error"
  timestamp: string
  href?: string
}

type ItemAction = {
  id: string
  label: string
  variant?: "primary" | "secondary" | "ghost"
  risk?: "read" | "local_write" | "external_write" | "destructive"
  confirm?: unknown
  disabledReason?: string
}

const DOT_COLORS: Record<string, string> = {
  success: "bg-green-500",
  info: "bg-blue-500",
  warning: "bg-yellow-500",
  error: "bg-red-500",
}

export function ListWidget(props: {
  data: ActivityItem[]
  emptyState?: { title?: string; description?: string }
  layout?: "stack" | "tool-grid"
  itemActions?: ItemAction[]
  actionState?: (
    action: ItemAction,
    item: ActivityItem,
  ) => {
    visible: boolean
    disabled: boolean
    disabledReason?: string
    pending: boolean
  }
  onItemAction?: (action: ItemAction, item: ActivityItem) => void
}) {
  const toolGrid = () => props.layout === "tool-grid"
  const columnCount = () => toolGrid() ? (props.data.length >= 9 ? 3 : props.data.length >= 4 ? 2 : 1) : 1
  const containerClass = () => {
    if (!toolGrid()) return "flex flex-col gap-3"
    if (columnCount() === 3) return "grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-3"
    if (columnCount() === 2) return "grid grid-cols-1 gap-2.5 md:grid-cols-2"
    return "grid grid-cols-1 gap-2.5"
  }
  const itemClass = () =>
    toolGrid()
      ? "flex min-w-0 items-start gap-3 rounded-md border border-border-weak-base bg-background-secondary/70 px-3 py-2.5 shadow-xs"
      : "flex items-start gap-3"
  const titleClass = () => toolGrid() ? "truncate text-13-medium text-text-strong" : "truncate text-sm text-text-base"

  return (
    <Show
      when={props.data.length}
      fallback={
        <div
          data-dashboard-empty-state=""
          class="rounded-lg border border-dashed border-border-weak-base bg-background-secondary/65 px-3 py-4 text-center text-xs text-text-dimmed"
        >
          <div class="flex flex-col gap-1">
            <span>{props.emptyState?.title ?? "No activity yet"}</span>
            <Show when={props.emptyState?.description}>
              <span class="text-[11px]">{props.emptyState?.description}</span>
            </Show>
          </div>
        </div>
      }
    >
      <div data-list-layout={props.layout ?? "stack"} data-list-columns={String(columnCount())} class={containerClass()}>
        <For each={props.data}>
          {(item) => (
            <div data-list-item class={itemClass()}>
              <div class={`shrink-0 mt-1.5 w-2 h-2 rounded-full ${DOT_COLORS[item.status] ?? "bg-gray-500"}`} />
              <div class="flex-1 min-w-0">
                <Show when={item.href} fallback={<p class={titleClass()}>{item.title}</p>}>
                  <a href={item.href} class={`${titleClass()} text-text-info`}>
                    {item.title}
                  </a>
                </Show>
                <Show when={item.description}>
                  <p class="text-xs text-text-base mt-0.5 line-clamp-2">{item.description}</p>
                </Show>
                <p class="text-xs text-text-dimmed mt-0.5">{item.timestamp}</p>
              </div>
              <Show when={props.itemActions?.length}>
                <div class="flex shrink-0 gap-2">
                  <For
                    each={(props.itemActions ?? []).filter(
                      (action) => props.actionState?.(action, item).visible ?? true,
                    )}
                  >
                    {(action) => {
                      const state = () => props.actionState?.(action, item)
                      return (
                        <Button
                          size="small"
                          variant={action.variant ?? "secondary"}
                          disabled={Boolean(state()?.disabled || state()?.pending)}
                          title={state()?.disabled ? (state()?.disabledReason ?? action.disabledReason) : undefined}
                          onClick={() => props.onItemAction?.(action, item)}
                        >
                          {state()?.pending ? `${action.label}...` : action.label}
                        </Button>
                      )
                    }}
                  </For>
                </div>
              </Show>
            </div>
          )}
        </For>
      </div>
    </Show>
  )
}
